/**
 * Angeforderte Bildbreite (imgproxy `rs:fit:N`) fuer Bild-Ebenen — nach
 * BILDSCHIRM, nicht nach Datei (2026-09-08, Lauras WK-Fund "Titelbild mobil
 * verpixelt, am Desktop scharf").
 *
 * Die alte Formel (PPI-Erbe, seit Renderer-Extraktion #54) teilte die
 * DATEI-Breite durch die Pixeldichte und halbierte grosse Bilder noch einmal:
 * ein iPhone (dpr 3) bekam das 3200er-Titelfoto als rs:fit:533 auf 1179
 * physische Pixel, ein Retina-Desktop (dpr 2) rs:fit:800, nur ein dpr-1-
 * Desktop die 1600. Hoehere Pixeldichte ergab KLEINERE Bilder.
 *
 * Jetzt: benoetigte Breite = laengste Viewport-Kante × Pixeldichte (Vollbild-
 * Ebenen decken den Viewport, im Hochformat zaehlt die Hoehe), auf eine feste
 * Stufenleiter aufgerundet. Feste Stufen statt freier Breiten, damit der
 * Cache-Warmer beim Publish exakt diese Ops vorwaermen kann (sonst kalte
 * sharp-Transformation beim ersten Ansehen = "Bild-Hakeln", 2026-08-11).
 *
 * Oberste Stufe 2048 = MOBILE_TEXTUR_KANTE (geraet.ts): mehr laedt auf Touch-
 * Geraeten ohnehin nicht auf die GPU, und am Desktop ist 2048 fuer heutige
 * Viewports (bis 2880 physisch bei 1440@2x) sichtbar scharf.
 *
 * ⚠️ LOCKSTEP: `src/user-scrollys/user-scrollys.service.ts`
 * (RENDERER_BILD_STUFEN + rendererProcessingStufen) spiegelt diese Leiter fuer
 * den Cache-Warmer. Wer hier eine Stufe aendert, aendert sie dort mit.
 */
export const BILD_BREITEN_STUFEN: readonly number[] = [800, 1200, 1600, 2048];

/**
 * Kleinste Stufe, die `viewportLongEdgeCss × dpr` abdeckt; darueber die
 * oberste Stufe. Ungueltige Eingaben (SSR, 0-Viewport) → oberste Stufe.
 */
export function bildBreitenStufe(
  viewportWidthCss: number,
  viewportHeightCss: number,
  dpr: number,
): number {
  const kante = Math.max(
    Number(viewportWidthCss) || 0,
    Number(viewportHeightCss) || 0,
  );
  const dichte = Number(dpr) > 0 ? Number(dpr) : 1;
  const benoetigt = kante * dichte;
  const oberste = BILD_BREITEN_STUFEN[BILD_BREITEN_STUFEN.length - 1];
  if (!(benoetigt > 0)) return oberste;
  for (const stufe of BILD_BREITEN_STUFEN) {
    if (stufe >= benoetigt) return stufe;
  }
  return oberste;
}

/**
 * TEXTUR-BUDGET AUF TOUCH-GERAETEN (Jans iPhone-Absturz 16.09.2026, Everest-
 * Scrolly mit 124 Bild-Ebenen, mit Benedikt abgestimmt):
 *
 * Die Stufe nach Bildschirm (oben) stimmt PRO Bild — ein iPhone (812 x dpr 3)
 * landet auf 2048. Bei 124 Bildern sind das aber ~1,5 GB entpackte Texturen,
 * WebKit beendet die Seite (Safari UND Chrome iOS, auch im frischen Tab).
 * Vor #703 bekam das iPhone 533 px breite Bilder (~100 MB) und lief.
 *
 * Regel: auf Touch-Geraeten die groesste Stufe, bei der
 *   anzahlBilder x Stufenpixel (4:3 angenommen) <= TOUCH_TEXTUR_BUDGET_PX
 * bleibt. 20 Bilder → 2048 bleibt (Lauras Schaerfe), 124 Bilder → 800.
 * Die Leiter bleibt unveraendert (Cache-Warmer-Lockstep, s. oben).
 */
export const TOUCH_TEXTUR_BUDGET_PX = 60e6;

/** Geschaetzte Pixel einer Stufe (Breite x 3/4 Hoehe). */
export function stufenPixel(stufe: number): number {
  return stufe * stufe * 0.75;
}

export function bildBreitenStufeMitBudget(
  viewportWidthCss: number,
  viewportHeightCss: number,
  dpr: number,
  anzahlBilder: number,
  touchGeraet: boolean,
): number {
  const stufe = bildBreitenStufe(viewportWidthCss, viewportHeightCss, dpr);
  const n = Number(anzahlBilder) || 0;
  if (!touchGeraet || n <= 0) return stufe;
  let gewaehlt = BILD_BREITEN_STUFEN[0];
  for (const kandidat of BILD_BREITEN_STUFEN) {
    if (kandidat > stufe) break;
    if (n * stufenPixel(kandidat) <= TOUCH_TEXTUR_BUDGET_PX) gewaehlt = kandidat;
  }
  return gewaehlt;
}
